// src/components/InviteToConsortiumDialog.tsx
// ---------------------------------------------------------------------------
// Self-contained "Invite to consortium" dialog.
//
// Provisions the verified 2-membership shape (see
// core/services/consortium.provision_consortium_member): the invitee gets
// VIEW+EDIT across the WHOLE consortium (a membership on the consortium env,
// which is export-disabled so it grants no download) PLUS DOWNLOAD on the ONE
// selected home institution (a second membership carrying can_export=True).
//
// Deliberately NOT the "grant across network" action, which would over-grant
// download uniformly on every centre.
//
// Isolated from ManageEnvironments so another session editing that file is not
// disturbed. It can either be handed the consortium's centres (to avoid a
// redundant fetch) or fetch the overview itself.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import HubIcon from "@mui/icons-material/Hub";
import { useSnackbar } from "notistack";
import { useTranslation } from "../i18n";
import api from "../api/api";
import logger from "utils/logger";
import {
  athenaDarkPaperSx,
  athenaBackdropSx,
  athenaDarkTitleSx,
  athenaDarkContentSx,
  athenaDarkActionsSx,
  athenaDarkPrimaryButtonSx,
  athenaDarkSecondaryButtonSx,
} from "./layout/AthenaDialogStyles";

interface Centre {
  id: string;
  name: string;
}

interface InviteToConsortiumDialogProps {
  open: boolean;
  onClose: () => void;
  /** The consortium environment id the invite is scoped to. */
  consortiumEnvId: string;
  /** Optional display name for the header subtitle. */
  consortiumName?: string;
  /**
   * The child institutions the admin can pick as the invitee's home centre.
   * When omitted the dialog fetches them from the consortium overview.
   */
  centres?: Centre[];
  /** Called after a successful invite (e.g. to refetch the overview). */
  onInvited?: () => void;
}

// Roles that grant VIEW+EDIT. The provision applies this role to BOTH the
// consortium membership and the home-centre membership, so only roles at or
// above Editor make sense here. Editor is the default and the recommended one.
const ROLE_OPTIONS: { value: string; labelKey: string; labelDefault: string }[] = [
  { value: "editor", labelKey: "consortium.role_editor", labelDefault: "Editor (view + edit)" },
  { value: "admin", labelKey: "consortium.role_admin", labelDefault: "Admin (view + edit + manage)" },
  { value: "owner", labelKey: "consortium.role_owner", labelDefault: "Owner (full control)" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteToConsortiumDialog: React.FC<InviteToConsortiumDialogProps> = ({
  open,
  onClose,
  consortiumEnvId,
  consortiumName,
  centres: centresProp,
  onInvited,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [homeEnvId, setHomeEnvId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [centres, setCentres] = useState<Centre[]>(centresProp || []);
  const [centresLoading, setCentresLoading] = useState(false);

  // Reset the form each time the dialog opens so a stale entry is never reused.
  useEffect(() => {
    if (open) {
      setEmail("");
      setRole("editor");
      setHomeEnvId("");
      setSubmitting(false);
    }
  }, [open]);

  // Keep the centre list in sync with the prop; fetch it ourselves only when the
  // caller did not supply one.
  useEffect(() => {
    if (!open) return;
    if (centresProp && centresProp.length) {
      setCentres(centresProp);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setCentresLoading(true);
      try {
        const res = await api.get(
          `/environments/${consortiumEnvId}/consortium/overview/`,
        );
        if (!cancelled) {
          setCentres(
            (res.data?.centres || []).map((c: { id: string | number; name: string }) => ({
              id: String(c.id),
              name: c.name,
            })),
          );
        }
      } catch (err) {
        logger.error("InviteToConsortiumDialog: failed to load centres", err);
        if (!cancelled) {
          enqueueSnackbar(
            t("consortium.invite_centres_error", "Could not load the list of institutions."),
            { variant: "error" },
          );
        }
      } finally {
        if (!cancelled) setCentresLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, centresProp, consortiumEnvId, enqueueSnackbar, t]);

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email]);
  const canSubmit = emailValid && !!homeEnvId && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/environments/${consortiumEnvId}/consortium/invite/`, {
        email: email.trim(),
        home_env_id: homeEnvId,
        role,
      });
      enqueueSnackbar(
        t(
          "consortium.invite_success",
          "Invitation sent. The member now has view and edit across the network, and download on their home institution.",
        ),
        { variant: "success" },
      );
      onInvited?.();
      onClose();
    } catch (err: any) {
      // Backend errors arrive either as {"error": "..."} (ConsortiumError) or as a
      // DRF ValidationError (string or array). Surface whichever is present.
      const data = err?.response?.data;
      const message =
        data?.error ||
        (Array.isArray(data) ? data[0] : typeof data === "string" ? data : null) ||
        t("consortium.invite_error", "The invitation was refused and nothing was changed.");
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, consortiumEnvId, email, homeEnvId, role, enqueueSnackbar, t, onInvited, onClose]);

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: athenaDarkPaperSx }}
      BackdropProps={{ sx: athenaBackdropSx }}
    >
      <DialogTitle sx={athenaDarkTitleSx}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(theme.palette.primary.main, 0.15),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
            flexShrink: 0,
          }}
        >
          <HubIcon sx={{ color: theme.palette.primary.light }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t("consortium.invite_title", "Invite to consortium")}
          </Typography>
          {consortiumName && (
            <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.6) }}>
              {consortiumName}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={athenaDarkContentSx} dividers>
        <Typography variant="body2" sx={{ mb: 2, color: alpha(theme.palette.common.white, 0.85) }}>
          {t(
            "consortium.invite_explainer",
            "The member gets view and edit across every centre in the network, and download rights on the one home institution you select below.",
          )}
        </Typography>

        <TextField
          fullWidth
          size="small"
          type="email"
          label={t("consortium.invite_email_label", "Email")}
          placeholder={t("consortium.invite_email_placeholder", "user@hospital.org")}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!email && !emailValid}
          helperText={
            !!email && !emailValid
              ? t("consortium.invite_email_invalid", "Enter a valid email address.")
              : " "
          }
          sx={{ mb: 1.5 }}
          disabled={submitting}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={submitting}>
          <InputLabel>{t("consortium.invite_role_label", "Role")}</InputLabel>
          <Select
            value={role}
            label={t("consortium.invite_role_label", "Role")}
            onChange={(e) => setRole(e.target.value as string)}
          >
            {ROLE_OPTIONS.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {t(r.labelKey, r.labelDefault)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" disabled={submitting || centresLoading}>
          <InputLabel>
            {t("consortium.invite_home_label", "Home institution (download)")}
          </InputLabel>
          <Select
            value={homeEnvId}
            label={t("consortium.invite_home_label", "Home institution (download)")}
            onChange={(e) => setHomeEnvId(e.target.value as string)}
            renderValue={(value) =>
              centres.find((c) => c.id === value)?.name || (value as string)
            }
          >
            {centresLoading && (
              <MenuItem value="" disabled>
                {t("consortium.invite_home_loading", "Loading institutions...")}
              </MenuItem>
            )}
            {!centresLoading && centres.length === 0 && (
              <MenuItem value="" disabled>
                {t("consortium.invite_home_empty", "No institutions available.")}
              </MenuItem>
            )}
            {centres.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions sx={athenaDarkActionsSx}>
        <Button onClick={onClose} disabled={submitting} sx={athenaDarkSecondaryButtonSx}>
          {t("common.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={athenaDarkPrimaryButtonSx}
          startIcon={
            submitting ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : undefined
          }
        >
          {t("consortium.invite_submit", "Send invite")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteToConsortiumDialog;
